import React from "react";
import {
    Box,
    Chip,
    Container,
    Grid,
    Paper,
    Stack,
    Typography,
} from "@mui/material";

import { sectionSubtitleSx, sectionTitleSx } from "./homeStyles";

function renderStatusColor(type) {
    if (type === "critical") {
        return {
            softBg: "rgba(239, 68, 68, 0.10)",
            mainBg: "rgba(239, 68, 68, 0.14)",
            color: "#b91c1c",
            border: "rgba(239, 68, 68, 0.22)",
        };
    }

    if (type === "warning") {
        return {
            softBg: "rgba(245, 158, 11, 0.12)",
            mainBg: "rgba(245, 158, 11, 0.16)",
            color: "#b45309",
            border: "rgba(245, 158, 11, 0.26)",
        };
    }

    return {
        softBg: "rgba(34, 197, 94, 0.10)",
        mainBg: "rgba(34, 197, 94, 0.14)",
        color: "#15803d",
        border: "rgba(34, 197, 94, 0.22)",
    };
}


function StatusRow({ item }) {
    const colors = renderStatusColor(item.type);

    return (
        <Paper
            elevation={0}
            sx={{
                px: { xs: 1.1, md: 1.4 },
                py: 1,
                borderRadius: 2.5,
                border: `1px solid ${colors.border}`,
                bgcolor: colors.bg,
            }}
        >

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        md: "150px 130px 1fr",
                    },
                    gap: { xs: 1, md: 1.2 },
                    alignItems: "stretch",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.8,
                        flexWrap: "wrap",
                        gridColumn: { xs: "1 / -1", md: "auto" },
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: 800,
                            color: colors.color,
                            fontSize: { xs: "0.8rem", md: "0.84rem" },
                            lineHeight: 1.2,
                            textAlign: "left",
                        }}
                    >
                        {item.title}
                    </Typography>

                    {item.level ? (
                        <Chip
                            label={item.level}
                            size="small"
                            sx={{
                                display: { xs: "flex", md: "none" },
                                height: 22,
                                "& .MuiChip-label": { px: 0.9 },
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                bgcolor:
                                    item.type === "critical"
                                        ? "rgba(239,68,68,0.14)"
                                        : item.type === "warning"
                                        ? "rgba(245,158,11,0.16)"
                                        : "rgba(34,197,94,0.14)",
                                color:
                                    item.type === "critical"
                                        ? "#b91c1c"
                                        : item.type === "warning"
                                        ? "#b45309"
                                        : "#15803d",
                                borderRadius: 1.5,
                            }}
                        />
                    ) : (
                        <Chip
                            label="Соответствует"
                            size="small"
                            sx={{
                                display: { xs: "flex", md: "none" },
                                height: 22,
                                "& .MuiChip-label": { px: 0.9 },
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                bgcolor: "rgba(34,197,94,0.14)",
                                color: "#15803d",
                                borderRadius: 1.5,
                            }}
                        />
                    )}
                </Box>

                <Box
                    sx={{
                        display: { xs: "none", md: "flex" },
                        alignItems: "center",
                    }}
                >
                    {item.level ? (
                        <Chip
                            label={item.level}
                            size="small"
                            sx={{
                                height: 22,
                                "& .MuiChip-label": { px: 0.9 },
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                bgcolor:
                                    item.type === "critical"
                                        ? "rgba(239,68,68,0.14)"
                                        : item.type === "warning"
                                        ? "rgba(245,158,11,0.16)"
                                        : "rgba(34,197,94,0.14)",
                                color:
                                    item.type === "critical"
                                        ? "#b91c1c"
                                        : item.type === "warning"
                                        ? "#b45309"
                                        : "#15803d",
                                borderRadius: 1.5,
                            }}
                        />
                    ) : (
                        <Chip
                            label="Соответствует"
                            size="small"
                            sx={{
                                height: 22,
                                "& .MuiChip-label": { px: 0.9 },
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                bgcolor: "rgba(34,197,94,0.14)",
                                color: "#15803d",
                                borderRadius: 1.5,
                            }}
                        />
                    )}
                </Box>

                <Box
                    sx={{
                        gridColumn: { xs: "1 / -1", md: "auto" },
                        bgcolor: "rgba(255,255,255,0.72)",
                        border: "1px solid rgba(255,255,255,0.55)",
                        borderRadius: 2,
                        px: 1,
                        py: 0.85,
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        sx={{
                            color: "#475569",
                            lineHeight: 1.35,
                            fontSize: { xs: "0.78rem", md: "0.84rem" },
                            textAlign: "left",
                        }}
                    >
                        {item.description}
                    </Typography>
                </Box>
            </Box>

        </Paper>
    );
}

function StatusCard({ title, items }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 1.5, md: 2 },
                borderRadius: 3.5,
                border: "1px solid rgba(148, 163, 184, 0.18)",
                bgcolor: "#ffffff",
                boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
                height: "100%",
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 800,
                    color: "#0f172a",
                    mb: 1.6,
                    fontSize: { xs: "1rem", md: "1.05rem" },
                    lineHeight: 1.3,
                }}
            >
                {title}
            </Typography>

            <Stack spacing={0.8}>
                {items.map((item) => (
                    <StatusRow key={`${item.type}-${item.title}`} item={item} />
                ))}
            </Stack>
        </Paper>
    );
}

function HomeStatuses() {
    const accountingStatuses = [
        {
            title: "НЕТ В УЧЕТЕ",
            level: "Критично",
            type: "critical",
            description: "Запись есть в банковских платежах, но отсутствует в учетной системе.",
        },
        {
            title: "НЕТ В БАНКЕ",
            level: "Критично",
            type: "critical",
            description: "Запись есть в учете, но отсутствует в банковских платежах.",
        },
        {
            title: "РАЗНИЦА",
            level: "Критично",
            type: "critical",
            description: "ИИН найден в обеих системах, но итоговые суммы не совпадают.",
        },
        {
            title: "РАЗНЫЕ ФИО",
            level: "Предупреждение",
            type: "warning",
            description: "ФИО по одному ИИН отличается между банковскими и учетными данными.",
        },
        {
            title: "НЕСКОЛЬКО ФИО",
            level: "Предупреждение",
            type: "warning",
            description: "По одному ИИН выявлено несколько вариантов ФИО.",
        },
        {
            title: "РАЗНЫЕ СЧЕТА",
            level: "Предупреждение",
            type: "warning",
            description: "Набор счетов в банке и в учете не совпадает.",
        },
        {
            title: "РАЗНОЕ РАСПРЕДЕЛЕНИЕ СУММ",
            level: "Предупреждение",
            type: "warning",
            description: "Суммы по счетам или вариантам записей распределены по-разному.",
        },
        {
            title: "НЕСКОЛЬКО СЧЕТОВ",
            level: "Предупреждение",
            type: "warning",
            description: "У одного ИИН выявлено несколько счетов.",
        },
        {
            title: "ОК",
            level: "",
            type: "success",
            description: "Основные и дополнительные проверки не выявили расхождений.",
        },
    ];

    const hrStatuses = [
        {
            title: "НЕТ В КАДРАХ",
            level: "Критично",
            type: "critical",
            description: "Сотрудник найден в банковских платежах, но отсутствует в кадровой системе.",
        },
        {
            title: "НЕТ В БАНКЕ",
            level: "Критично",
            type: "critical",
            description: "Сотрудник найден в кадровой системе, но отсутствует в банковских платежах.",
        },
        {
            title: "РАЗНЫЕ ФИО",
            level: "Предупреждение",
            type: "warning",
            description: "ИИН найден в обеих системах, но ФИО отличаются.",
        },
        {
            title: "ОК",
            level: "",
            type: "success",
            description: "ИИН и ФИО совпадают между банковскими и кадровыми данными.",
        },
    ];

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
            <Box textAlign="center" sx={{ mb: 4 }}>
                <Typography variant="h3" sx={sectionTitleSx}>
                    Какие ошибки и риски находит система
                </Typography>

                <Typography
                    sx={{
                        ...sectionSubtitleSx,
                        maxWidth: 880,
                    }}
                >
                    Qarzhy классифицирует отклонения по степени важности и сразу
                    показывает, какие расхождения требуют немедленного внимания,
                    какие являются предупреждением, а какие записи полностью
                    соответствуют данным учета или кадровой системы.
                </Typography>
            </Box>

            <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, xl: 7 }}>
                    <StatusCard
                        title="Статусы сверки с бухгалтерским учетом"
                        items={accountingStatuses}
                    />
                </Grid>

                <Grid size={{ xs: 12, xl: 5 }}>
                    <StatusCard
                        title="Статусы сверки с кадровыми данными"
                        items={hrStatuses}
                    />
                </Grid>
            </Grid>
        </Container>
    );
}

export default HomeStatuses;